import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import SessionExpired from "@/src/lib/components/app/SessionExpired";
import Icon from "../../lib/components/custom/Icon";
import { Image } from "../../lib/components/custom/Image";
import { RestaurantClosedBanner } from "../../lib/components/custom/RestaurantClosedBanner";
import { Badge } from "../../lib/components/ui/badge";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../lib/components/ui/select";
import { Separator } from "../../lib/components/ui/separator";
import { Skeleton } from "../../lib/components/ui/skeleton";
import { calculateExtrasTotal, getExtraPrice } from "../../lib/constants";
import {
	getNextOpenTime,
	isRestaurantOpen,
	RESTAURANT_HOURS,
} from "../../lib/constants/restaurant-hours";
import { useApi } from "../../lib/hooks/api/use-api";
import { useOrderFlowStore } from "../../lib/hooks/use-store";
import { calculateOnlinePaymentFee } from "../../lib/utils/pricing";
import Layout from "../layout";

const parseGmtOffsetToMinutes = (offsetLabel: string): number => {
	const match = offsetLabel.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/i);
	if (!match) return 0;
	const sign = match[1] === "-" ? -1 : 1;
	const hours = Number(match[2] || 0);
	const minutes = Number(match[3] || 0);
	return sign * (hours * 60 + minutes);
};

const getTimeZoneOffsetMinutes = (date: Date, timeZone: string): number => {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		timeZoneName: "shortOffset",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).formatToParts(date);
	const offset =
		parts.find((part) => part.type === "timeZoneName")?.value || "GMT";
	return parseGmtOffsetToMinutes(offset);
};

const getTimeZoneParts = (date: Date, timeZone: string) => {
	const parts = new Intl.DateTimeFormat("en-GB", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		weekday: "short",
	}).formatToParts(date);

	const get = (type: Intl.DateTimeFormatPartTypes) =>
		parts.find((part) => part.type === type)?.value || "";

	const weekdayMap: Record<string, number> = {
		Sun: 0,
		Mon: 1,
		Tue: 2,
		Wed: 3,
		Thu: 4,
		Fri: 5,
		Sat: 6,
	};

	return {
		year: Number(get("year")),
		month: Number(get("month")),
		day: Number(get("day")),
		hour: Number(get("hour")),
		minute: Number(get("minute")),
		dayOfWeek: weekdayMap[get("weekday")] ?? 0,
	};
};

const zonedDateTimeToUtcDate = (
	year: number,
	month: number,
	day: number,
	hour: number,
	minute: number,
	timeZone: string,
): Date => {
	const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
	const offsetMins = getTimeZoneOffsetMinutes(new Date(utcGuess), timeZone);
	return new Date(utcGuess - offsetMins * 60_000);
};

export default function Review() {
	const { t } = useTranslation();
	const { auth, cart, users, orders, payments } = useApi();
	const { selectedAddressId, setOrderDetails, clearOrderFlow, orderDetails } =
		useOrderFlowStore();
	const navigate = useNavigate();

	const { data: session } = auth.useSession();
	const {
		data: cartData,
		isLoading,
		error,
		refetch,
	} = cart.getByUserId(session?.user?.id || "");
	const { data: addressesData } = users.addresses.getAll();

	const items = cartData?.cartItems;
	const addresses = addressesData?.addresses || [];
	const selectedAddress = addresses.find(
		(addr) => addr.id === selectedAddressId,
	);
	const itemCount = items?.length;

	const [deliveryFee, setDeliveryFee] = useState(0);
	const { mutate: validateAddress } = users.addresses.validateAddress;

	const subtotal = items?.reduce((sum: number, item) => {
		const basePrice = parseFloat(item.menuSize.price) * item.quantity;
		const extrasPrice =
			calculateExtrasTotal(item.extras, item.menuSize.sizeName) * item.quantity;
		return sum + basePrice + extrasPrice;
	}, 0);

	const [isPlacingOrder, setIsPlacingOrder] = useState(false);
	const [paymentMethod, setPaymentMethod] = useState<"stripe" | "cash">(
		"stripe",
	);
	const total = subtotal ? subtotal + deliveryFee : 0;
	const onlinePaymentFee =
		paymentMethod === "stripe" ? calculateOnlinePaymentFee(total) : 0;
	const finalTotal = total + onlinePaymentFee;

	// Restaurant hours check
	const restaurantOpen = isRestaurantOpen();
	const nextOpenTime = getNextOpenTime();

	// Scheduling state - stored as absolute UTC ISO, selected in Europe/Berlin local time
	const [scheduledForIso, setScheduledForIso] = useState<string | null>(null);

	// Generate time options for scheduling (every hour from open to close, future times only)
	const getTimeOptions = () => {
		const options = [];
		const berlinNow = getTimeZoneParts(new Date(), RESTAURANT_HOURS.timezone);
		const dayOfWeek = berlinNow.dayOfWeek;
		const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
		const hours = isWeekend
			? RESTAURANT_HOURS.weekend
			: RESTAURANT_HOURS.weekday;

		const [openHour] = hours.open.split(":").map(Number);
		const [closeHour, closeMinute] = hours.close.split(":").map(Number);
		const nowMinutes = berlinNow.hour * 60 + berlinNow.minute;
		const closeTimeMinutes = closeHour * 60 + closeMinute;

		for (let hour = openHour; hour <= closeHour; hour++) {
			const timeStr = `${hour.toString().padStart(2, "0")}:00`;
			const optionMinutes = hour * 60;

			// Only include future times
			if (optionMinutes > nowMinutes && optionMinutes < closeTimeMinutes) {
				const displayTime = new Date(
					Date.UTC(2000, 0, 1, hour, 0),
				).toLocaleTimeString("en-US", {
					hour: "numeric",
					minute: "2-digit",
					hour12: true,
					timeZone: "UTC",
				});
				options.push({ value: timeStr, label: displayTime });
			}
		}

		return options;
	};

	const timeOptions = getTimeOptions();

	useEffect(() => {
		// If no address selected, redirect back to checkout
		if (!selectedAddressId) {
			navigate({ to: "/checkout" });
		}
	}, [selectedAddressId, navigate]);

	// Get delivery fee from API when selected address changes (zone-based, source of truth)
	useEffect(() => {
		if (!selectedAddress?.zipCode) {
			setDeliveryFee(0);
			return;
		}
		validateAddress(
			{
				streetAddress: selectedAddress.streetAddress,
				city: selectedAddress.city,
				zipCode: selectedAddress.zipCode,
				country: selectedAddress.country || "Germany",
			},
			{
				onSuccess: (result) => {
					setDeliveryFee(result.isValid ? (result.deliveryFee ?? 0) : 0);
				},
				onError: () => setDeliveryFee(0),
			},
		);
	}, [
		selectedAddress?.zipCode,
		selectedAddress?.streetAddress,
		selectedAddress?.city,
		selectedAddress?.country,
		validateAddress,
	]);

	// Authentication check
	const user = session?.user;
	if (!user?.id) {
		return <SessionExpired />;
	}

	const handlePlaceOrder = async () => {
		if (!session?.user?.id) {
			toast.error(t("review.errors.sign_in_required"));
			navigate({ to: "/auth/signin" });
			return;
		}

		if (!selectedAddress) {
			toast.error(t("review.errors.select_address"));
			return;
		}

		setIsPlacingOrder(true);

		try {
			if (!items) {
				toast.error(t("review.errors.no_items"));
				return;
			}
			const orderItems = items?.map((item) => {
				if (!item.menuItemId || !item.menuSizeId) {
					throw new Error(
						`Invalid menu item data: menuItemId and menuSizeId are required`,
					);
				}

				return {
					menuItemId: item.menuItemId,
					menuSizeId: item.menuSizeId,
					quantity: item.quantity,
					extras: (item.extras || []).map((extra) => {
						// Validate extra fields
						if (!extra.menuExtraId || !extra.quantity) {
							throw new Error(
								`Invalid extra data: menuExtraId and quantity are required`,
							);
						}

						return {
							menuExtraId: extra.menuExtraId,
							quantity: extra.quantity,
							unitPrice: getExtraPrice(extra, item.menuSize.sizeName),
						};
					}),
				};
			});

			const orderData = {
				items,
				address: selectedAddress,
				total: finalTotal,
				subtotal,
				deliveryFee,
				onlinePaymentFee,
				orderItems,
			};

			if (orderDetails) {
				console.log("Clearing existing order details from previous session");
				setOrderDetails(null);
			}

			sessionStorage.setItem("pendingOrder", JSON.stringify(orderData));

			if (paymentMethod === "cash") {
				const scheduledForValue = scheduledForIso || undefined;

				if (!orderItems) {
					toast.error(t("review.errors.no_items"));
					return;
				}
				const cashOrderResult = await orders.create.mutateAsync({
					userId: session.user.id,
					addressId: selectedAddressId,
					orderItems,
					deliveryInstructions: selectedAddress.instructions || undefined,
					paymentMethod: "cash",
					scheduledFor: scheduledForValue,
				});

				clearOrderFlow();
				toast.success(t("review.toast.order_success"));
				window.location.href = `/confirmation?order_id=${cashOrderResult.order.id}`;
				return;
			}

			// Stripe payment flow
			const baseUrl = window.location.origin;
			const scheduledForValue = scheduledForIso || undefined;

			const checkoutResponse = await payments.createCheckout.mutateAsync({
				amount: finalTotal,
				currency: "eur",
				userId: session.user.id,
				addressId: selectedAddressId,
				orderItems,
				deliveryInstructions: selectedAddress.instructions || undefined,
				scheduledFor: scheduledForValue,
				successUrl: `${baseUrl}/confirmation?session_id={CHECKOUT_SESSION_ID}`,
				cancelUrl: `${baseUrl}/review`,
			});

			// Redirect to Stripe Checkout
			if (checkoutResponse.url) {
				window.location.href = checkoutResponse.url;
			} else {
				throw new Error("No checkout URL returned");
			}
		} catch (error) {
			toast.error(
				paymentMethod === "cash"
					? t("review.errors.order_failed_cash")
					: t("review.errors.order_failed_stripe"),
			);
			console.error("Error creating order:", error);
			setIsPlacingOrder(false);
		}
	};

	// Authentication check
	if (!session?.user?.id) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full flex items-center justify-center">
					<div className="max-w-2xl mx-auto text-center">
						<Card className="p-12">
							<div className="flex flex-col items-center gap-4">
								<Icon
									name="LogIn"
									className="w-16 h-16 text-muted-foreground"
								/>
								<div>
									<h2 className="text-xl font-semibold mb-2">
										{t("review.sign_in_required.header")}
									</h2>
									<p className="text-muted-foreground mb-6">
										{t("review.sign_in_required.subheader")}
									</p>
									<Button asChild>
										<Link to="/auth/signin">
											{t("review.sign_in_required.button")}
										</Link>
									</Button>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	// Loading state
	if (isLoading) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full">
					<div className="max-w-4xl mx-auto">
						{/* Header Skeleton */}
						<div className="mb-8">
							<Skeleton className="h-8 w-48 mb-2" />
							<Skeleton className="h-4 w-64" />
						</div>

						<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
							{/* Order Details Skeleton */}
							<div className="lg:col-span-2 space-y-6">
								<Card className="p-6">
									<Skeleton className="h-6 w-32 mb-6" />
									<div className="space-y-4">
										{Array.from({ length: 3 }).map((_, i) => (
											<Skeleton key={i} className="h-20 w-full rounded-lg" />
										))}
									</div>
								</Card>
								<Card className="p-6">
									<Skeleton className="h-6 w-40 mb-6" />
									<div className="space-y-2">
										<Skeleton className="h-4 w-full" />
										<Skeleton className="h-4 w-3/4" />
										<Skeleton className="h-4 w-1/2" />
									</div>
								</Card>
							</div>

							{/* Summary Skeleton */}
							<Card className="p-6">
								<Skeleton className="h-6 w-32 mb-4" />
								<div className="space-y-3 mb-6">
									{Array.from({ length: 3 }).map((_, i) => (
										<Skeleton key={i} className="h-4 w-full" />
									))}
								</div>
								<Skeleton className="h-4 w-full mb-4" />
								<Skeleton className="h-10 w-full" />
							</Card>
						</div>
					</div>
				</div>
			</Layout>
		);
	}

	// Error state
	if (error) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full flex items-center justify-center">
					<div className="max-w-2xl mx-auto text-center">
						<Card className="p-12">
							<div className="flex flex-col items-center gap-4">
								<Icon
									name="CircleAlert"
									className="w-16 h-16 text-destructive"
								/>
								<div>
									<h2 className="text-xl font-semibold mb-2">
										{t("review.error.header")}
									</h2>
									<p className="text-muted-foreground mb-6">
										{t("review.error.subheader")}
									</p>
									<Button onClick={() => refetch()}>
										{t("review.error.retry")}
									</Button>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	if (!selectedAddress || itemCount === 0) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full flex items-center justify-center">
					<div className="max-w-2xl mx-auto text-center">
						<Card className="p-12">
							<div className="flex flex-col items-center gap-4">
								<Icon
									name="ShoppingCart"
									className="w-16 h-16 text-muted-foreground"
								/>
								<div>
									<h2 className="text-xl font-semibold mb-2">
										{t("review.unable_to_load.header")}
									</h2>
									<p className="text-muted-foreground mb-6">
										{t("review.unable_to_load.subheader")}
									</p>
									<Button asChild>
										<Link to="/">{t("review.unable_to_load.browse_menu")}</Link>
									</Button>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="p-8 border rounded-large bg-gradient-main min-h-full">
				<div className="max-w-4xl md:max-w-6xl mx-auto">
					{/* Header */}
					<div className="mb-8">
						<h1 className="text-3xl font-manrope font-semibold">
							{t("review.header")}
						</h1>
						<p className="text-muted-foreground mt-1">
							{t("review.subheader")}
						</p>
					</div>

					{/* Restaurant Closed Banner */}
					{!restaurantOpen && (
						<div className="mb-6">
							<RestaurantClosedBanner nextOpenTime={nextOpenTime} />
						</div>
					)}

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
						{/* Order Details */}
						<div className="lg:col-span-2 space-y-6">
							{/* Order Items */}
							<Card className="p-6">
								<h2 className="text-xl font-semibold mb-6">
									{t("review.order_items.header")}
								</h2>
								<div className="space-y-4">
									{items?.map((item) => (
										<div
											key={item.id}
											className="flex flex-col md:flex-row md:gap-4 p-4 border rounded-lg"
										>
											<div className="self-center md:self-start">
												{item.menuItem.imageUrl ? (
													<Image
														src={item.menuItem.imageUrl}
														alt={item.menuItem.name}
														className="w-28 object-cover rounded-full aspect-square"
													/>
												) : (
													<div className="w-28 rounded-lg bg-accent flex items-center justify-center">
														<Icon
															name="Image"
															className="w-7 h-7 text-muted-foreground"
														/>
													</div>
												)}
											</div>
											<Separator className="my-2 mb-4 md:hidden" />
											<div className="flex-1">
												<h3 className="font-semibold">{item.menuItem.name}</h3>
												<div className="text-sm text-muted-foreground">
													{t("review.order_items.size")}:{" "}
													{item.menuSize.sizeName} •{" "}
													{t("review.order_items.qty")}: {item.quantity}
												</div>
												{item.extras && item.extras.length > 0 && (
													<div className="flex flex-wrap gap-1.5 mt-2">
														{item.extras?.map((extra, idx: number) => {
															const extraPrice = getExtraPrice(
																extra,
																item.menuSize.sizeName,
															);
															return (
																<Badge
																	key={`${extra.id}-${idx}`}
																	variant="secondary"
																	className="text-xs"
																>
																	{extra.name}
																	{extraPrice > 0 &&
																		` +€${extraPrice.toFixed(2)}`}
																	{extra.quantity > 1 && ` x${extra.quantity}`}
																</Badge>
															);
														})}
													</div>
												)}
												<div className="flex items-center gap-2 mt-2">
													<Badge variant="secondary">
														{item.menuItem.category}
													</Badge>
													<span className="font-medium">
														€
														{(
															(parseFloat(item.menuSize.price) +
																calculateExtrasTotal(
																	item.extras || [],
																	item.menuSize.sizeName,
																)) *
															item.quantity
														).toFixed(2)}
													</span>
												</div>
											</div>
										</div>
									))}
								</div>
							</Card>

							{/* Delivery Address */}
							<Card className="p-6">
								<h2 className="text-xl font-semibold mb-6">
									{t("review.delivery_address.header")}
								</h2>
								{selectedAddress && (
									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<h3 className="font-semibold text-lg">
												{selectedAddress.label}
											</h3>
											{selectedAddress.isDefault && (
												<Badge variant="secondary" className="text-xs">
													{t("review.order_items.default_badge")}
												</Badge>
											)}
										</div>
										<div className="text-sm space-y-1">
											<p className="font-medium text-foreground">
												{selectedAddress.streetAddress}
											</p>
											<p>
												{selectedAddress.city}, {selectedAddress.state}{" "}
												{selectedAddress.zipCode}
											</p>
											{selectedAddress.country !== "USA" && (
												<p>{selectedAddress.country}</p>
											)}
											{selectedAddress.phone && (
												<p className="text-muted-foreground">
													{t("review.delivery_address.phone")}:{" "}
													{selectedAddress.phone}
												</p>
											)}
											{selectedAddress.instructions && (
												<div className="mt-3 p-3 bg-accent rounded-lg">
													<div className="text-sm font-medium mb-1">
														{t("review.delivery_address.delivery_instructions")}
													</div>
													<div className="text-sm text-muted-foreground italic">
														"{selectedAddress.instructions}"
													</div>
												</div>
											)}
										</div>
									</div>
								)}
								<div className="mt-4">
									<Button variant="outline" size="sm" asChild>
										<Link to="/checkout">
											{t("review.delivery_address.change_address")}
										</Link>
									</Button>
								</div>
							</Card>

							{/* Order Scheduling - only when restaurant is closed */}
							{!restaurantOpen && (
								<Card className="p-6">
									<h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
										<Icon name="Clock" className="w-5 h-5" />
										Schedule Your Order
									</h2>
									<div className="space-y-4">
										<p className="text-sm text-muted-foreground">
											Choose a time for your order to be prepared.
										</p>
										<div className="space-y-2">
											<label
												htmlFor="scheduled-time"
												className="text-sm font-medium"
											>
												Select Time
											</label>
											<Select
												value={
													scheduledForIso
														? (() => {
																const parts = getTimeZoneParts(
																	new Date(scheduledForIso),
																	RESTAURANT_HOURS.timezone,
																);
																return `${parts.hour.toString().padStart(2, "0")}:${parts.minute.toString().padStart(2, "0")}`;
															})()
														: ""
												}
												onValueChange={(timeStr) => {
													const [hours, minutes] = timeStr
														.split(":")
														.map(Number);
													const now = new Date();
													const berlinNow = getTimeZoneParts(
														now,
														RESTAURANT_HOURS.timezone,
													);
													const scheduledDate = zonedDateTimeToUtcDate(
														berlinNow.year,
														berlinNow.month,
														berlinNow.day,
														hours,
														minutes,
														RESTAURANT_HOURS.timezone,
													);

													if (
														Number.isNaN(scheduledDate.getTime()) ||
														scheduledDate <= now
													) {
														return;
													}
													setScheduledForIso(scheduledDate.toISOString());
												}}
											>
												<SelectTrigger>
													<SelectValue placeholder="Choose a time" />
												</SelectTrigger>
												<SelectContent>
													{timeOptions.map((option) => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										{scheduledForIso && (
											<div className="p-3 bg-accent rounded-lg">
												<div className="flex items-center justify-between">
													<div>
														<div className="text-sm font-medium">
															Scheduled for today at
														</div>
														<div className="text-sm text-muted-foreground">
															{new Date(scheduledForIso).toLocaleTimeString(
																"en-US",
																{
																	hour: "numeric",
																	minute: "2-digit",
																	hour12: true,
																	timeZone: RESTAURANT_HOURS.timezone,
																},
															)}
														</div>
													</div>
													<Button
														variant="secondary"
														size="sm"
														onClick={() => setScheduledForIso(null)}
														className="text-xs"
													>
														Clear
													</Button>
												</div>
											</div>
										)}
									</div>
								</Card>
							)}
						</div>

						{/* Order Summary / Payment */}
						<div className="lg:col-span-1">
							<Card className="p-6 sticky top-4">
								<h2 className="text-xl font-semibold mb-4">
									{t("review.summary.header")}
								</h2>

								<div className="space-y-3 mb-6">
									<div className="flex justify-between">
										<span>{t("review.summary.subtotal")}</span>
										<span>€{subtotal?.toFixed(2)}</span>
									</div>
									<div className="flex justify-between">
										<span>{t("review.summary.delivery")}</span>
										<span>
											{deliveryFee > 0 ? (
												`€${deliveryFee.toFixed(2)}`
											) : (
												<span className="text-primary">FREE</span>
											)}
										</span>
									</div>
									{paymentMethod === "stripe" && (
										<div className="flex justify-between">
											<span>Online service fee</span>
											<span>€{onlinePaymentFee.toFixed(2)}</span>
										</div>
									)}
								</div>

								<Separator className="mb-4" />

								<div className="flex justify-between text-lg font-semibold mb-6">
									<span>{t("review.summary.total")}</span>
									<span>€{finalTotal.toFixed(2)}</span>
								</div>

								<div className="space-y-3 mb-6">
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<Icon name="Clock" className="w-4 h-4" />
										<span>{t("review.summary.estimated_delivery")}</span>
									</div>
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<Icon
											name={
												paymentMethod === "stripe" ? "CreditCard" : "Banknote"
											}
											className="w-4 h-4"
										/>
										<span>
											{paymentMethod === "stripe"
												? t("review.summary.secure_payment")
												: t("review.summary.cash_payment")}
										</span>
									</div>
								</div>

								{/* Payment Method Selection */}
								<div className="mb-6">
									<h3 className="text-sm font-medium mb-3">
										{t("review.payment_method.header")}
									</h3>
									<div className="grid grid-cols-2 gap-3">
										<button
											type="button"
											onClick={() => setPaymentMethod("stripe")}
											className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-all ${
												paymentMethod === "stripe"
													? "border-primary bg-primary/5 ring-1 ring-primary"
													: "border-border hover:border-primary/50"
											}`}
										>
											<Icon
												name="CreditCard"
												className={`w-5 h-5 ${paymentMethod === "stripe" ? "text-primary" : "text-muted-foreground"}`}
											/>
											<span
												className={`text-sm font-medium ${paymentMethod === "stripe" ? "text-primary" : ""}`}
											>
												{t("review.payment_method.card")}
											</span>
										</button>
										<button
											type="button"
											onClick={() => setPaymentMethod("cash")}
											className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-all ${
												paymentMethod === "cash"
													? "border-primary bg-primary/5 ring-1 ring-primary"
													: "border-border hover:border-primary/50"
											}`}
										>
											<Icon
												name="Banknote"
												className={`w-5 h-5 ${paymentMethod === "cash" ? "text-primary" : "text-muted-foreground"}`}
											/>
											<span
												className={`text-sm font-medium ${paymentMethod === "cash" ? "text-primary" : ""}`}
											>
												{t("review.payment_method.cash")}
											</span>
										</button>
									</div>
								</div>

								<Button
									className="w-full"
									size="lg"
									onClick={handlePlaceOrder}
									disabled={isPlacingOrder}
									variant="primary"
								>
									{isPlacingOrder ? (
										<>
											<Icon
												name="LoaderCircle"
												className="w-4 h-4 animate-spin"
											/>
											{paymentMethod === "stripe"
												? t("review.place_order.redirecting")
												: t("review.place_order.placing")}
										</>
									) : (
										<>
											{paymentMethod === "stripe"
												? t("review.place_order.continue_payment")
												: t("review.place_order.place_order")}
											<Icon name="ChevronRight" className="w-4 h-4 ml-2" />
										</>
									)}
								</Button>

								<div className="text-center mt-4">
									<Button variant="link" asChild className="text-sm">
										<Link to="/checkout">
											<Icon name="ChevronLeft" className="w-4 h-4" />
											{t("review.place_order.back_to_checkout")}
										</Link>
									</Button>
								</div>
							</Card>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
}
